import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiSendForm, ApiError } from '../api/client';
import { todayIso } from '../lib/format';

/**
 * Add/edit expense. Ports frmStargymPages and its four siblings.
 *
 * Differences from the desktop dialog, all deliberate:
 *  - The article field is a free-text input with suggestions rather than a
 *    combo box. The desktop version saved artikulli = 0 when the typed name
 *    was not in the list (frmStargymPages.cs:202), producing rows that show
 *    "N/A" forever. The server now creates the article instead.
 *  - Editing without re-picking a file keeps the stored invoice. The desktop
 *    version rewrote all three file columns from form state on every save.
 */

interface Article {
  id: number;
  artikulli: string;
}

interface ExpenseDetail {
  ID: number;
  data: string;
  artikulli: number | null;
  artikulliName: string | null;
  cmimi: number;
  file_name: string | null;
  periudha: string | null;
  sasia: number | null;
  hasFile: number;
}

interface Props {
  biznesi: string;
  businessName: string;
  kategoriaId: number;
  categoryName: string;
  expenseId: number | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function ExpenseModal({
  biznesi,
  businessName,
  kategoriaId,
  categoryName,
  expenseId,
  onClose,
  onSaved,
}: Props) {
  const isEdit = expenseId !== null;

  const [data, setData] = useState(todayIso);
  const [artikulli, setArtikulli] = useState('');
  const [cmimi, setCmimi] = useState('');
  const [sasia, setSasia] = useState('');
  const [periudha, setPeriudha] = useState('');
  const [auto, setAuto] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [removeFile, setRemoveFile] = useState(false);
  const [existingFileName, setExistingFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scheduleWarning, setScheduleWarning] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const articlesQuery = useQuery({
    queryKey: ['articles', biznesi, kategoriaId],
    queryFn: () =>
      apiGet<{ articles: Article[]; periudhaOptions: string[] }>(
        `/shpenzimet/aktuale/${biznesi}/artikujt`,
        { kategoriaId },
      ),
  });

  const detailQuery = useQuery({
    queryKey: ['expense', expenseId],
    queryFn: () => apiGet<ExpenseDetail>(`/shpenzimet/aktuale/shpenzimi/${expenseId}`),
    enabled: isEdit,
  });

  useEffect(() => {
    const d = detailQuery.data;
    if (!d) return;
    setData(d.data.slice(0, 10));
    setArtikulli(d.artikulliName ?? '');
    setCmimi(String(d.cmimi ?? ''));
    setSasia(d.sasia == null ? '' : String(d.sasia));
    setPeriudha(d.periudha ?? '');
    setExistingFileName(Number(d.hasFile) === 1 ? (d.file_name ?? 'fatura') : null);
  }, [detailQuery.data]);

  // Warn before an "auto" save overwrites an existing schedule, matching the
  // MessageBox at frmStargymPages.cs:455.
  useEffect(() => {
    const match = articlesQuery.data?.articles.find((a) => a.artikulli === artikulli);
    if (!match) {
      setScheduleWarning(null);
      return;
    }
    let cancelled = false;
    apiGet<{ schedule: { periudha: string | null } | null }>(
      `/shpenzimet/aktuale/${biznesi}/orari/${match.id}`,
    )
      .then((res) => {
        if (cancelled) return;
        setScheduleWarning(
          res.schedule
            ? `Ky artikull është caktuar tashmë të shtohet automatikisht çdo ${res.schedule.periudha}. Nëse aktivizoni "Auto", të dhënat e vjetra zëvendësohen.`
            : null,
        );
      })
      .catch(() => {
        if (!cancelled) setScheduleWarning(null);
      });
    return () => {
      cancelled = true;
    };
  }, [artikulli, articlesQuery.data, biznesi]);

  // Escape closes, matching the desktop dialog's Close button behaviour.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!artikulli.trim()) {
      setError('Ju lutem zgjidhni një artikull.');
      return;
    }
    const price = Number(cmimi);
    if (cmimi.trim() === '' || !Number.isFinite(price)) {
      setError('Ju lutem vendosni një çmim të vlefshëm.');
      return;
    }
    if (sasia.trim() !== '' && !Number.isFinite(Number(sasia))) {
      setError('Ju lutem vendosni një sasi të vlefshme.');
      return;
    }
    if (auto && !periudha) {
      setError('Ju lutem zgjidhni një periudhë.');
      return;
    }

    const form = new FormData();
    form.set('kategoriaId', String(kategoriaId));
    form.set('data', data);
    form.set('artikulli', artikulli.trim());
    form.set('cmimi', String(price));
    if (sasia.trim() !== '') form.set('sasia', String(Number(sasia)));
    if (periudha) form.set('periudha', periudha);
    if (auto) form.set('auto', 'true');
    if (removeFile) form.set('removeFile', 'true');
    if (file) form.set('file', file);

    setSaving(true);
    try {
      if (isEdit) {
        await apiSendForm(`/shpenzimet/aktuale/${biznesi}/shpenzimet/${expenseId}`, form, 'PUT');
      } else {
        await apiSendForm(`/shpenzimet/aktuale/${biznesi}/shpenzimet`, form, 'POST');
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Ruajtja dështoi. Provoni përsëri.');
    } finally {
      setSaving(false);
    }
  }

  const periudhaOptions = articlesQuery.data?.periudhaOptions ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4">
      <form
        onSubmit={handleSubmit}
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl"
      >
        <div className="mb-4">
          <h2 className="text-lg font-semibold">
            {isEdit ? 'Ndrysho shpenzimin' : 'Shto shpenzim'}
          </h2>
          <p className="text-sm text-muted">
            {businessName} — {categoryName}
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="m-data">
              Data
            </label>
            <input
              id="m-data"
              type="date"
              value={data}
              onChange={(e) => setData(e.target.value)}
              required
              className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="m-cmimi">
              Çmimi
            </label>
            <input
              id="m-cmimi"
              inputMode="decimal"
              value={cmimi}
              onChange={(e) => setCmimi(e.target.value.replace(/[^\d.]/g, ''))}
              required
              className="tnum w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="m-artikulli">
              Artikulli
            </label>
            {/*
              A closed list, not free text. Every article combo in the desktop is
              DropDownStyle = DropDownList (frmStargymPages.Designer.cs:201, and
              the same in the Fitimet, Planifikuara and Pagesat dialogs), so a
              name that is not already in the catalogue cannot be entered. New
              articles are created on the Nënkategoritë screen and only there.
            */}
            <select
              id="m-artikulli"
              value={artikulli}
              onChange={(e) => setArtikulli(e.target.value)}
              required
              className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            >
              <option value="">Zgjidhni një artikull</option>
              {articlesQuery.data?.articles.map((a) => (
                <option key={a.id} value={a.artikulli}>
                  {a.artikulli}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="m-sasia">
              Sasia
            </label>
            <input
              id="m-sasia"
              inputMode="decimal"
              value={sasia}
              onChange={(e) => setSasia(e.target.value.replace(/[^\d.]/g, ''))}
              className="tnum w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="m-periudha">
              Periudha
            </label>
            <select
              id="m-periudha"
              value={periudha}
              onChange={(e) => setPeriudha(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            >
              <option value="">—</option>
              {periudhaOptions.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={auto}
                onChange={(e) => setAuto(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300"
              />
              Shto automatikisht sipas periudhës
            </label>
            {auto && scheduleWarning && (
              <div className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {scheduleWarning}
              </div>
            )}
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="m-file">
              Fatura
            </label>
            {existingFileName && !file && !removeFile && (
              <div className="mb-2 flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                <span className="truncate text-slate-700">{existingFileName}</span>
                <button
                  type="button"
                  onClick={() => setRemoveFile(true)}
                  className="ml-2 shrink-0 text-xs font-medium text-red-600 hover:underline"
                >
                  Hiq
                </button>
              </div>
            )}
            {removeFile && (
              <div className="mb-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                Fatura ekzistuese do të hiqet kur të ruani.
              </div>
            )}
            <input
              id="m-file"
              type="file"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setRemoveFile(false);
              }}
              className="w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium hover:file:bg-slate-200"
            />
          </div>
        </div>

        {error && (
          <div role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Anulo
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-brand-500 px-5 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60"
          >
            {saving ? 'Duke ruajtur…' : 'Ruaj'}
          </button>
        </div>
      </form>
    </div>
  );
}
